/**
 * Popup Coordination + Page Targeting — master-only panel (2026-08-03).
 *
 * Per-dealer configuration for the three FLOATING popups: Test Drive,
 * Smart Offers, Recall Check. Each product has independent include/
 * exclude URL patterns. A per-dealer priority order decides which
 * popup wins when two or more qualify on the same page.
 *
 * INLINE surfaces (Unlock in-card CTA, Recall Check inline mode, Recall
 * Check hosted landing page) are OUTSIDE this system and never affected.
 *
 * Dealers cannot see or edit any of this — it's master-only.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Loader2, Save, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";

const PRODUCT_META = {
  recall_check: {
    label: "Recall Check",
    color: "text-amber-700",
    swatch: "bg-amber-100 border-amber-300",
    hint: "Popup surface. Inline mode + hosted /recall/<slug> page are unaffected.",
  },
  smart_offers: {
    label: "Smart Offers",
    color: "text-emerald-700",
    swatch: "bg-emerald-100 border-emerald-300",
    hint: "Return-visitor offer popup.",
  },
  test_drive: {
    label: "Test Drive",
    color: "text-sky-700",
    swatch: "bg-sky-100 border-sky-300",
    hint: "Gift-card test-drive popup.",
  },
};

const PRODUCT_TO_COLS = {
  recall_check: { include: "rc_include_patterns", exclude: "rc_exclude_patterns" },
  smart_offers: { include: "so_include_patterns", exclude: "so_exclude_patterns" },
  test_drive:   { include: "td_include_patterns", exclude: "td_exclude_patterns" },
};

export default function PopupCoordinationPanel({ dealer }) {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState({});      // { td_include_patterns: "...", ... }
  const [priority, setPriority] = useState([]);   // ordered array of product keys

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/admin/dealers/${dealer.id}/popup-config`, { method: "GET" });
      const data = r?.data || r;
      setConfig(data);
      setPriority(data.priority || ["recall_check", "smart_offers", "test_drive"]);
      // Seed drafts from RESOLVED patterns (defaults filled in) so the
      // user sees what's actually applied. Raw NULL → resolved defaults.
      const seed = {};
      Object.keys(PRODUCT_TO_COLS).forEach((key) => {
        const cols = PRODUCT_TO_COLS[key];
        const raw = data.raw?.[key] || {};
        const resolved = data.products?.[key] || {};
        seed[cols.include] = raw.include_patterns != null
          ? raw.include_patterns
          : (resolved.include || []).join("\n");
        seed[cols.exclude] = raw.exclude_patterns != null
          ? raw.exclude_patterns
          : (resolved.exclude || []).join("\n");
      });
      setDrafts(seed);
    } catch (e) {
      toastErr("Could not load popup coordination settings.");
    } finally {
      setLoading(false);
    }
  }, [dealer?.id]);

  useEffect(() => { load(); }, [load]);

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= priority.length) return;
    const next = priority.slice();
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item);
    setPriority(next);
  };

  const resetToDefaults = () => {
    if (!config?.defaults) return;
    setPriority(config.defaults.priority);
    const seed = {};
    Object.keys(PRODUCT_TO_COLS).forEach((key) => {
      const cols = PRODUCT_TO_COLS[key];
      seed[cols.include] = (config.defaults.include[key] || []).join("\n");
      seed[cols.exclude] = (config.defaults.exclude[key] || []).join("\n");
    });
    setDrafts(seed);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        popup_priority: priority,
        ...drafts,
      };
      const r = await apiFetch(`/admin/dealers/${dealer.id}/popup-config`, {
        method: "PATCH",
        data: payload,
      });
      const data = r?.data || r;
      setConfig(data);
      toastOk("Popup coordination saved.");
    } catch (e) {
      toastErr(e?.response?.data?.detail || e?.message || "Could not save popup coordination.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-600 py-8"
           data-testid="popup-coord-loading">
        <Loader2 className="animate-spin" size={16} /> Loading popup coordination…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="popup-coord-panel">
      {/* Explainer */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-[13px] text-slate-700">
        <div className="font-semibold text-slate-900 mb-1">
          Popup Coordination · Master-only · Dealer sees none of this
        </div>
        <div className="leading-relaxed">
          Each floating popup (Test Drive, Smart Offers, Recall Check) can be
          restricted to specific URL paths. Use <code className="bg-white px-1 rounded border">*</code> as
          a wildcard (e.g. <code className="bg-white px-1 rounded border">/inventory/*</code>,{" "}
          <code className="bg-white px-1 rounded border">*/vehicle/*</code>,{" "}
          <code className="bg-white px-1 rounded border">*service*</code>). One pattern per line.
          Exclude beats include. If two popups both qualify on the same page, the
          higher-priority one wins and the others are suppressed for that page.
        </div>
        <div className="mt-2 text-slate-500 text-[12px]">
          Inline surfaces are outside this system: Unlock&apos;s in-card <em>Our Best Price</em>{" "}
          buttons, Recall Check&apos;s inline mode, and its hosted <code>/recall/&lt;slug&gt;</code> page
          are never affected.
        </div>
      </div>

      {/* Priority order */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-[15px] font-semibold flex items-center gap-2"
                     data-testid="popup-coord-priority-title">
            Popup Priority
            <Badge variant="outline" className="font-normal text-[11px]">
              highest → lowest
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2" data-testid="popup-coord-priority-list">
            {priority.map((key, idx) => {
              const meta = PRODUCT_META[key];
              const active = config?.products?.[key]?.active;
              return (
                <li
                  key={key}
                  data-testid={`popup-coord-priority-item-${key}`}
                  className={`flex items-center gap-3 rounded-md border px-3 py-2 ${meta.swatch}`}
                >
                  <span className="text-slate-500 font-mono text-[12px] w-4">{idx + 1}</span>
                  <span className={`flex-1 font-medium ${meta.color}`}>{meta.label}</span>
                  <Badge
                    variant="outline"
                    className={active ? "text-emerald-700 border-emerald-300" : "text-slate-500 border-slate-300"}
                    data-testid={`popup-coord-priority-active-${key}`}
                  >
                    {active ? "active" : "off"}
                  </Badge>
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    className="p-1 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`popup-coord-priority-up-${key}`}
                    aria-label={`Move ${meta.label} up`}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    disabled={idx === priority.length - 1}
                    onClick={() => move(idx, 1)}
                    className="p-1 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`popup-coord-priority-down-${key}`}
                    aria-label={`Move ${meta.label} down`}
                  >
                    <ArrowDown size={14} />
                  </button>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      {/* Per-product page rules */}
      {priority.map((key) => {
        const cols = PRODUCT_TO_COLS[key];
        const meta = PRODUCT_META[key];
        return (
          <Card key={key} className="border-slate-200"
                data-testid={`popup-coord-rules-${key}`}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-[15px] font-semibold flex items-center gap-2 ${meta.color}`}>
                {meta.label} · Page Rules
              </CardTitle>
              <div className="text-[12px] text-slate-500">{meta.hint}</div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">
                  Include patterns
                  <span className="text-slate-400 font-normal ml-1">
                    (blank = any path)
                  </span>
                </label>
                <Textarea
                  value={drafts[cols.include] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [cols.include]: e.target.value }))}
                  placeholder="/inventory/*&#10;*/vehicle/*"
                  rows={4}
                  data-testid={`popup-coord-include-${key}`}
                  className="font-mono text-[12px]"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">
                  Exclude patterns
                  <span className="text-slate-400 font-normal ml-1">
                    (exclude beats include)
                  </span>
                </label>
                <Textarea
                  value={drafts[cols.exclude] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [cols.exclude]: e.target.value }))}
                  placeholder="*/admin/*&#10;*/private/*"
                  rows={4}
                  data-testid={`popup-coord-exclude-${key}`}
                  className="font-mono text-[12px]"
                />
              </div>
            </CardContent>
          </Card>
        );
      })}

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-200">
        <Button
          variant="outline"
          onClick={resetToDefaults}
          disabled={saving}
          data-testid="popup-coord-reset-btn"
        >
          <RotateCcw size={14} className="mr-1.5" />
          Reset to defaults
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="popup-coord-save-btn"
        >
          {saving ? <Loader2 className="animate-spin mr-1.5" size={14} />
                  : <Save size={14} className="mr-1.5" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}
